import React from 'react';

export type MatchBracket = 'excellent' | 'good' | 'moderate' | 'limited';

export interface MatchScoreBarProps {
  score: number | null | undefined;
  size?: 'sm' | 'md' | 'lg';
  bracket?: string;
  onClick?: () => void;
  ariaLabel?: string;
}

const SIZE_MAP: Record<NonNullable<MatchScoreBarProps['size']>, { px: number; stroke: number; fontScore: number; fontPct: number; fontBracket: number }> = {
  sm: { px: 48, stroke: 5, fontScore: 14, fontPct: 8, fontBracket: 10 },
  md: { px: 80, stroke: 7, fontScore: 22, fontPct: 10, fontBracket: 11 },
  lg: { px: 120, stroke: 9, fontScore: 32, fontPct: 12, fontBracket: 13 },
};

const BRACKET_COLORS: Record<MatchBracket, string> = {
  excellent: '#16a34a',
  good: '#2563eb',
  moderate: '#d97706',
  limited: '#dc2626',
};

const BRACKET_LABELS: Record<MatchBracket, string> = {
  excellent: 'Excellent',
  good: 'Good',
  moderate: 'Moderate',
  limited: 'Limited',
};

export function deriveBracket(score: number): MatchBracket {
  if (score >= 85) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 55) return 'moderate';
  return 'limited';
}

const MatchScoreBar: React.FC<MatchScoreBarProps> = ({
  score,
  size = 'md',
  bracket,
  onClick,
  ariaLabel,
}) => {
  const dims = SIZE_MAP[size];
  const radius = (dims.px - dims.stroke) / 2;
  const center = dims.px / 2;
  const circumference = 2 * Math.PI * radius;

  const hasScore = typeof score === 'number' && !Number.isNaN(score);
  const safeScore = hasScore ? Math.max(0, Math.min(100, Number(score))) : 0;
  const effectiveBracket = (bracket as MatchBracket) || (hasScore ? deriveBracket(safeScore) : 'limited');
  const color = BRACKET_COLORS[effectiveBracket] || BRACKET_COLORS.good;
  const dashOffset = circumference - (safeScore / 100) * circumference;

  const rounded = hasScore ? Math.round(safeScore) : null;
  const showBracketUnder = size !== 'sm';
  const label = ariaLabel || (hasScore ? `Match score ${rounded}%` : 'Match score not available');

  const ring = (
    <svg width={dims.px} height={dims.px} role="img" aria-label={label}>
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={dims.stroke}
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={hasScore ? color : '#cbd5e1'}
        strokeWidth={dims.stroke}
        strokeDasharray={hasScore ? `${circumference} ${circumference}` : `4 4`}
        strokeDashoffset={hasScore ? dashOffset : 0}
        strokeLinecap="round"
        transform={`rotate(-90 ${center} ${center})`}
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      <text
        x={center}
        y={center + (showBracketUnder ? -2 : 4)}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={dims.fontScore}
        fontWeight={700}
        fill={hasScore ? color : '#94a3b8'}
      >
        {hasScore ? rounded : '—'}
      </text>
      {hasScore && showBracketUnder && (
        <text
          x={center}
          y={center + dims.fontScore / 2 + 6}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={dims.fontPct}
          fill="#64748b"
        >
          %
        </text>
      )}
    </svg>
  );

  const content = (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: showBracketUnder ? 4 : 0 }}>
      {ring}
      {hasScore && showBracketUnder && (
        <span
          style={{
            fontSize: dims.fontBracket,
            color: color,
            fontWeight: 600,
            textTransform: 'capitalize',
            lineHeight: 1,
          }}
        >
          {BRACKET_LABELS[effectiveBracket]}
        </span>
      )}
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={`${label} — click for details`}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          borderRadius: 8,
          display: 'inline-flex',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(15, 23, 42, 0.04)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        }}
      >
        {content}
      </button>
    );
  }

  return <div aria-label={label}>{content}</div>;
};

export default MatchScoreBar;
